import {
	DEFAULT_LANG,
	type LanguageCode,
	SUPPORTED_LANGS,
} from "@/i18n/constants";

const SUPPORTED = SUPPORTED_LANGS as readonly string[];

/**
 * Removes a leading supported-locale segment (e.g. `/ar`, `/en`) from a
 * pathname. Unsupported leading segments and non-locale paths are returned
 * unchanged. A pathname consisting solely of a locale segment collapses to
 * `"/"`.
 */
const stripLeadingLocale = (pathname: string): string => {
	const match = pathname.match(/^\/([^/]+)(\/.*|$)/);
	if (match && match[1] && SUPPORTED.includes(match[1])) {
		return match[2] || "/";
	}
	return pathname;
};

/**
 * Canonical pathname builder used by Phase 2 link/href hooks and the
 * Phase 9 language switcher.
 *
 * Rules:
 * - `("/dashboard", "ar")`     → `"/ar/dashboard"`
 * - `("/ar/events/abc", "en")` → `"/events/abc"`  (prefix dropped — canonical)
 * - `("/en/dashboard", "en")`  → `"/dashboard"`   (canonicalize the alias)
 * - `("/ar/dashboard", "ar")`  → `"/ar/dashboard"` (no-op)
 * - `("/", "ar")`              → `"/ar"`
 * - `("/", "en")`              → `"/"`
 *
 * The function is pure and synchronous — it does not call
 * `i18n.changeLanguage` or read any global state. That keeps it usable from
 * any layer (hooks, services, route loaders, tests).
 */
const rewriteLocaleInPath = (
	pathname: string,
	targetLang: LanguageCode,
): string => {
	if (!pathname.startsWith("/")) return pathname;

	const canonical = stripLeadingLocale(pathname);
	if (targetLang === DEFAULT_LANG) return canonical;
	return canonical === "/" ? `/${targetLang}` : `/${targetLang}${canonical}`;
};

export { rewriteLocaleInPath, stripLeadingLocale };
